import { Controller } from "@hotwired/stimulus"

// The seating plan, as something you can move around in — sideways only.
//
// It used to scale the whole hall down until it fitted the page, which on a
// phone meant 90 seats at four pixels each: technically the whole plan,
// practically unusable. So it is drawn at its own size and this is a window onto
// it, opened on the seats.
//
// It opens showing all of it, scaled down to whatever width there is, and grows
// as tall as that needs. Zooming in is what crops it — sideways, because the box
// keeps growing downwards to hold whatever the zoom made taller. So nothing ever
// has to be dragged vertically: a drag fighting the page's own scrolling would
// be a worse problem than the one this solves.
export default class extends Controller {
    static targets = ["stage"]
    static values = {
        background: String,
        padding: { type: Number, default: 16 },
        margin: { type: Number, default: 32 },
        min: { type: Number, default: 0.3 },
        max: { type: Number, default: 3 },
    }

    connect() {
        this.scale = 1
        this.pan = { x: 0, y: 0 }
        this.pointers = new Map()
        this.pinch = null

        this.onPointerMove = this.onPointerMove.bind(this)
        this.onPointerUp = this.onPointerUp.bind(this)
        this.reset = this.reset.bind(this)
        window.addEventListener("pointermove", this.onPointerMove)
        window.addEventListener("pointerup", this.onPointerUp)
        window.addEventListener("pointercancel", this.onPointerUp)

        this.observer = new ResizeObserver(() => this.apply())
        this.observer.observe(this.element)

        this.measureBackground()
        this.reset()
        requestAnimationFrame(this.reset)
    }

    disconnect() {
        window.removeEventListener("pointermove", this.onPointerMove)
        window.removeEventListener("pointerup", this.onPointerUp)
        window.removeEventListener("pointercancel", this.onPointerUp)
        this.observer?.disconnect()
    }

    get stage() {
        return this.hasStageTarget ? this.stageTarget : this.element.firstElementChild
    }

    // The drawing is a background image, so its box comes from its proportions.
    measureBackground() {
        if (!this.backgroundValue) return

        const image = new Image()
        image.onload = () => {
            if (!image.naturalWidth) return

            const drawing = this.element.querySelector(".seating_plan_drawing")
            if (drawing && this.stage) {
                this.drawingHeight =
                    Math.round(this.stage.offsetWidth * (image.naturalHeight / image.naturalWidth))
                drawing.style.height = `${this.drawingHeight}px`
                this.reset()
            }
        }
        image.src = this.backgroundValue
    }

    // --- what there is to look at -------------------------------------------

    // Everything there is to see: the blocks, and the hall drawn behind them.
    contentBox() {
        const blocks = this.stage?.querySelectorAll(".seat_block")
        if (!blocks?.length) return null

        let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity
        blocks.forEach((block) => {
            left = Math.min(left, block.offsetLeft)
            top = Math.min(top, block.offsetTop)
            right = Math.max(right, block.offsetLeft + block.offsetWidth)
            bottom = Math.max(bottom, block.offsetTop + block.offsetHeight)
        })

        const drawing = this.drawingBox()
        if (drawing) {
            left = Math.min(left, drawing.left)
            top = Math.min(top, drawing.top)
            right = Math.max(right, drawing.left + drawing.width)
            bottom = Math.max(bottom, drawing.top + drawing.height)
        }

        // The block labels hang outside the box (see .block-label), so this pad
        // is not only breathing room.
        const pad = this.paddingValue + 16
        return { left: left - pad, top: top - pad, width: right - left + pad * 2, height: bottom - top + pad * 2 }
    }

    // Where the floorplan actually lands.
    //
    // Not its layout box: SeatingPlan#background_transform turns and shrinks it
    // about its own middle, so a plan laid out 1000x1330 can be drawn at a
    // quarter of that. Measuring the layout box instead put the empty difference
    // into the window as bands of graph paper with nothing in them.
    //
    // The four corners go through the computed matrix, which covers rotation as
    // well as scale without this having to know which was used.
    drawingBox() {
        const drawing = this.element.querySelector(".seating_plan_drawing")
        if (!drawing || !this.drawingHeight) return null

        const width = drawing.offsetWidth
        const height = this.drawingHeight
        if (!width || !height) return null

        const transform = getComputedStyle(drawing).transform
        const matrix = transform && transform !== "none"
            ? new DOMMatrixReadOnly(transform)
            : new DOMMatrixReadOnly()

        // transform-origin is the middle, so the corners are taken relative to it
        // and put back afterwards.
        const cx = width / 2
        const cy = height / 2
        let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity

        for (const [x, y] of [[0, 0], [width, 0], [0, height], [width, height]]) {
            const point = matrix.transformPoint({ x: x - cx, y: y - cy })
            left = Math.min(left, point.x + cx)
            right = Math.max(right, point.x + cx)
            top = Math.min(top, point.y + cy)
            bottom = Math.max(bottom, point.y + cy)
        }

        return { left, top, width: right - left, height: bottom - top }
    }

    // The seats alone, in the same shape as contentBox — what the window opens on
    // when the whole plan will not fit at a size anybody could use.
    seatBox() {
        const blocks = this.stage?.querySelectorAll(".seat_block")
        if (!blocks?.length) return null

        let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity
        blocks.forEach((block) => {
            left = Math.min(left, block.offsetLeft)
            top = Math.min(top, block.offsetTop)
            right = Math.max(right, block.offsetLeft + block.offsetWidth)
            bottom = Math.max(bottom, block.offsetTop + block.offsetHeight)
        })

        const pad = this.paddingValue + 16
        return { left: left - pad, top: top - pad, width: right - left + pad * 2, height: bottom - top + pad * 2 }
    }

    // Which of the two the window opens on.
    //
    // Room enough means room for all of it at the size it was drawn — nothing is
    // ever scaled down to make it fit. Shrinking is the thing this window exists
    // to avoid: the whole hall reduced until it technically fits is 90 seats at
    // four pixels each, which is a picture of a plan rather than a plan.
    //
    // Without that room it opens on the seats, still at their own size. The rest
    // of the hall is not thrown away — contentBox goes on bounding the panning,
    // so the doors and the bar are a drag to the side, and the zoom buttons still
    // pull back for an overview if somebody wants one.
    //
    // Decided by the room available rather than by the current zoom, so zooming
    // never flips the window between the two.
    viewBox() {
        const full = this.contentBox()
        const seats = this.seatBox()
        if (!full) return null
        if (!seats) return full

        return this.focused() ? seats : full
    }

    // True when there is not room for the whole plan, so the window is opened on
    // the seats instead.
    focused() {
        const full = this.contentBox()

        return !!full && !this.fits(full)
    }

    // Whether a box has room to be shown whole, at its own size.
    fits(box) {
        const width = this.element.clientWidth

        return !width || box.width + this.marginValue * 2 <= width
    }

    // Room under the plan for the zoom buttons, which float in the bottom corner.
    // With the whole hall on screen they have its empty edge to sit over; opened
    // on the seats they would sit on top of somebody's row.
    controlRoom() {
        if (!this.focused()) return 0

        const controls = this.element.querySelector(".seating-plan-viewer__zoom")
        if (!controls) return 0

        // The offset it is placed at, top and bottom of it.
        return controls.offsetHeight + this.paddingValue * 2
    }

    // --- placing it ---------------------------------------------------------

    // Opens on whatever viewBox decided, at the size the plan was drawn. Zooming
    // is a thing you ask for, not something the window does to you on the way in.
    reset() {
        const box = this.viewBox()
        if (!box) return

        const width = this.element.clientWidth
        if (!width) return

        this.scale = 1
        this.pan.x = (width - box.width * this.scale) / 2 - box.left * this.scale
        this.apply()
    }

    apply() {
        const stage = this.stage
        const view = this.viewBox()
        const world = this.contentBox()
        if (!stage || !view || !world) return

        // Only sideways is ours: the top of the view sits at the top of the
        // window, and the window is made tall enough for the rest of it. Panning
        // is bounded by the world, not the view, so opening on the seats still
        // leaves the rest of the hall a drag away.
        this.pan.y = -view.top * this.scale
        this.pan.x = this.clampX(world, this.element.clientWidth)

        this.element.style.height = `${Math.ceil(view.height * this.scale) + this.controlRoom()}px`
        this.element.classList.toggle("is-pannable", world.width * this.scale > this.element.clientWidth)

        stage.style.transformOrigin = "top left"
        stage.style.transform = `translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.scale})`
    }

    // Stops at the hall's edges plus a little air, so the plan cannot be dragged
    // off into the void and lost.
    clampX(box, viewport) {
        const margin = this.marginValue
        const width = box.width * this.scale
        const origin = -box.left * this.scale

        if (width + margin * 2 <= viewport) return origin + (viewport - width) / 2

        return Math.min(origin + margin, Math.max(origin + viewport - width - margin, this.pan.x))
    }

    // --- moving around ------------------------------------------------------

    start(event) {
        // The zoom buttons live inside the window; pressing one is not a drag.
        if (event.target.closest(".seating-plan-viewer__zoom")) return

        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })

        if (this.pointers.size === 2) {
            this.pinch = { distance: this.spread(), scale: this.scale, centre: this.centre() }
            return
        }

        this.drag = { x: event.clientX, pan: this.pan.x, moved: false }
    }

    onPointerMove(event) {
        if (!this.pointers.has(event.pointerId)) return
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })

        if (this.pinch && this.pointers.size >= 2) {
            this.zoomTo(this.pinch.scale * (this.spread() / this.pinch.distance), this.pinch.centre)
            return
        }

        if (!this.drag) return

        const dx = event.clientX - this.drag.x
        if (Math.abs(dx) > 3) {
            this.drag.moved = true
            this.element.classList.add("is-dragging")
        }

        this.pan.x = this.drag.pan + dx
        this.apply()
    }

    onPointerUp(event) {
        this.pointers.delete(event.pointerId)
        if (this.pointers.size < 2) this.pinch = null
        if (this.pointers.size === 0) {
            this.drag = null
            this.element.classList.remove("is-dragging")
        }
    }

    // A drag that moved must not also count as a click on the seat underneath.
    guard(event) {
        if (this.drag?.moved) {
            event.preventDefault()
            event.stopPropagation()
        }
    }

    spread() {
        const [a, b] = Array.from(this.pointers.values())
        return Math.hypot(a.x - b.x, a.y - b.y) || 1
    }

    centre() {
        const [a, b] = Array.from(this.pointers.values())
        return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
    }

    // --- zoom ---------------------------------------------------------------

    zoomIn() { this.zoomTo(this.scale * 1.25, this.viewportCentre()) }
    zoomOut() { this.zoomTo(this.scale / 1.25, this.viewportCentre()) }

    viewportCentre() {
        const box = this.element.getBoundingClientRect()
        return { x: box.left + box.width / 2, y: box.top + box.height / 2 }
    }

    // Keeps the point under the fingers (or the cursor) where it is, sideways.
    zoomTo(value, point) {
        const box = this.element.getBoundingClientRect()
        const x = point.x - box.left

        const next = Math.min(this.maxValue, Math.max(this.minValue, value))
        const ratio = next / this.scale

        this.pan.x = x - (x - this.pan.x) * ratio
        this.scale = next
        this.apply()
    }

    onWheel(event) {
        if (event.ctrlKey || event.metaKey) {
            event.preventDefault()
            this.zoomTo(this.scale * Math.exp(-event.deltaY / 200), { x: event.clientX, y: event.clientY })
            return
        }

        // A sideways wheel moves the plan; an upright one is the page's to
        // handle, and taking it would be the scroll-jacking this avoids.
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return

        event.preventDefault()
        this.pan.x -= event.deltaX
        this.apply()
    }
}
